import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import ConfirmDialog from '../../../components/ConfirmDialog';
import { useToast } from '../../../components/Toast';
import {
  CharacterAIRankItem,
  CharacterStyleResult,
  CharacterStyleResultRole,
  CharacterStyleWorkspace as CharacterStyleWorkspaceData,
} from '../../../types';
import {
  createCharacterStyling,
  fetchCastingCandidates,
  fetchStylingCandidates,
  generateCharacterStyleResult,
  getCharacterStyleWorkspace,
  listCharacterStyleResults,
  rankCastingCandidates,
  selectCastingCandidate,
  selectStylingCandidate,
  setCharacterStyleResultRole,
  updateCastingDescription,
  updateCastingTags,
  updateStylingDescription,
  updateStylingTags,
  retryCharacterStyleTags,
  uploadCastingCandidate,
  uploadCharacterStyleResult,
  uploadAssetConfigRefImage,
  uploadStylingCandidate,
  isApiError,
} from '../../../api';
import { tosThumbnail } from '../../../utils/tosImage';
import { useIsMobile } from '../../../utils/useIsMobile';
import { useImageUploadZone } from '../../../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../../../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../../../utils/imageFiles';
import CharacterStylePickerModal from './CharacterStylePickerModal';
import CharacterAIRankModal from './CharacterAIRankModal';
import CharacterStyleHistoryGrid from './CharacterStyleHistoryGrid';
import CharacterTagEditorModal from './CharacterTagEditorModal';
import type { BaseMetadataField, ResolvedBaseMetadata } from '../utils/baseMetadata';

interface CharacterStyleWorkspaceProps {
  open: boolean;
  projectId: string;
  characterName: string;
  charFixedPrompt: string;
  projectRefImages?: string[];
  platform?: string;
  model?: string;
  baseMetadata: ResolvedBaseMetadata;
  analysisAuthorityRevision: number;
  onBaseMetadataChanged?: () => void | boolean | Promise<void | boolean>;
  onClose: () => void;
}

const MAX_REFS = 6;
const PROCESSING_TAG_STATUSES = new Set(['pending', 'running', 'generating']);
const PROCESSING_FETCH_STATUSES = new Set(['fetching']);

function isWorkspaceProcessing(workspace?: CharacterStyleWorkspaceData | null): boolean {
  if (!workspace) return false;
  if (!workspace.casting || workspace.stylings.length === 0) return true;
  if (PROCESSING_TAG_STATUSES.has(workspace.casting.tagsStatus)) return true;
  if (PROCESSING_FETCH_STATUSES.has(workspace.casting.fetchStatus)) return true;
  return workspace.stylings.some(styling =>
    PROCESSING_TAG_STATUSES.has(styling.tagsStatus) ||
    PROCESSING_FETCH_STATUSES.has(styling.fetchStatus),
  );
}
const SELECTION_KEY_SEPARATOR = '\u001f';

function buildSelectionKey(
  projectId: string,
  characterName: string,
  stylingId?: string,
  castingImageHash?: string,
  stylingImageHash?: string,
): string {
  if (!stylingId || !castingImageHash || !stylingImageHash) return '';
  return [projectId, characterName, stylingId, castingImageHash, stylingImageHash].join(SELECTION_KEY_SEPARATOR);
}

function buildDefaultPrompt(
  fixedPrompt: string,
  castingPrompt: string,
  appearance: string,
  selectedStylePrompt: string,
): string {
  return [
    fixedPrompt,
    castingPrompt,
    appearance,
    selectedStylePrompt,
  ].filter(Boolean).join('\n');
}

function parseTagList(raw?: string): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(item => String(item)).filter(Boolean);
  } catch {
    // tagsFlat may also be persisted as a plain comma-separated string.
  }
  return raw.split(/[,，\n]/).map(item => item.trim()).filter(Boolean);
}

export default function CharacterStyleWorkspace({
  open,
  projectId,
  characterName,
  charFixedPrompt,
  projectRefImages = [],
  platform,
  model,
  baseMetadata,
  analysisAuthorityRevision,
  onBaseMetadataChanged,
  onClose,
}: CharacterStyleWorkspaceProps) {
  const { t } = useTranslation('projectDetail');
  const toast = useToast();
  const guardAspectRatio = useImageAspectRatioGuard();
  const isMobile = useIsMobile();
  const [workspace, setWorkspace] = useState<CharacterStyleWorkspaceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [creatingStyling, setCreatingStyling] = useState(false);
  const [activeStylingId, setActiveStylingId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [uploadedRefs, setUploadedRefs] = useState<string[]>([]);
  const [removedAdditionalRefs, setRemovedAdditionalRefs] = useState<string[]>([]);
  const [results, setResults] = useState<CharacterStyleResult[]>([]);
  const [picker, setPicker] = useState<'casting' | 'styling' | null>(null);
  const [aiRankOpen, setAiRankOpen] = useState(false);
  const [aiRankSelected, setAiRankSelected] = useState<string | undefined>(undefined);
  const [tagEditor, setTagEditor] = useState<'casting' | 'styling' | null>(null);
  const [savingTags, setSavingTags] = useState(false);
  const [workingSelectionKey, setWorkingSelectionKey] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescription, setEditDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const [, setDescriptionHovered] = useState(false);
  const [confirmDescEdit, setConfirmDescEdit] = useState(false);
  const [editingStylingDesc, setEditingStylingDesc] = useState(false);
  const [editStylingDesc, setEditStylingDesc] = useState('');
  const [confirmStylingDescEdit, setConfirmStylingDescEdit] = useState(false);
  const [savingStylingDesc, setSavingStylingDesc] = useState(false);
  const [, setStylingDescHovered] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [serverBaseConflictFields, setServerBaseConflictFields] = useState<BaseMetadataField[]>([]);
  const onBaseMetadataChangedRef = useRef(onBaseMetadataChanged);
  const baseMetadataRefreshFailureNotifiedRef = useRef(false);
  const resultsRequestSeqRef = useRef(0);
  const currentSelectionKeyRef = useRef('');
  const promptInitializedRef = useRef(false);
  const promptRef = useRef('');
  const promptDirtyRef = useRef(false);
  useEffect(() => {
    onBaseMetadataChangedRef.current = onBaseMetadataChanged;
  }, [onBaseMetadataChanged]);
  useEffect(() => {
    setServerBaseConflictFields([]);
  }, [analysisAuthorityRevision]);
  const effectiveBaseConflictFields = Array.from(new Set([
    ...baseMetadata.conflictFields,
    ...serverBaseConflictFields,
  ]));
  const castingBaseBlocked = effectiveBaseConflictFields.includes('baseRefImages') ||
    effectiveBaseConflictFields.includes('baseImageUrls');

  const reportBaseMetadataRefreshFailure = (error: unknown) => {
    console.error('[CharacterStyleWorkspace] base metadata authority refresh failed', error);
    if (baseMetadataRefreshFailureNotifiedRef.current) return;
    baseMetadataRefreshFailureNotifiedRef.current = true;
    toast.error(t('characterStyle.baseMetadataRefreshFailed'));
  };

  const refreshBaseMetadataAuthority = () => {
    let refresh: void | boolean | Promise<void | boolean>;
    try {
      refresh = onBaseMetadataChangedRef.current?.();
    } catch (error: unknown) {
      reportBaseMetadataRefreshFailure(error);
      return;
    }
    void Promise.resolve(refresh)
      .then(success => {
        if (success === false) {
          throw new Error('base metadata authority refresh returned false');
        }
        baseMetadataRefreshFailureNotifiedRef.current = false;
      })
      .catch(reportBaseMetadataRefreshFailure);
  };

  const captureCastingBaseConflict = (error: unknown): boolean => {
    if (!isApiError(error) || error.status !== 409 || error.code !== 'base_metadata_conflict') {
      return false;
    }
    const fields = (error.fields ?? []).filter(
      (field): field is BaseMetadataField => field === 'baseRefImages' || field === 'baseImageUrls',
    );
    if (fields.length === 0) return false;
    setServerBaseConflictFields(current => Array.from(new Set([...current, ...fields])));
    setTagEditor(null);
    toast.error(t('characterStyle.baseMetadataConflict'));
    refreshBaseMetadataAuthority();
    return true;
  };

  const notifyBaseMetadataChanged = () => {
    refreshBaseMetadataAuthority();
  };

  const reportCastingActionFailure = (error: unknown) => {
    if (!captureCastingBaseConflict(error)) {
      toast.error(t('characterStyle.baseMetadataActionFailed'));
    }
  };

  const activeStyling = useMemo(
    () => workspace?.stylings.find(styling => styling.id === activeStylingId) || workspace?.stylings[0],
    [workspace, activeStylingId],
  );
  const castingCandidates = workspace?.castingCandidates || [];
  const stylingCandidates = activeStyling ? (workspace?.stylingCandidates[activeStyling.id] || []) : [];
  const selectedCasting = workspace?.casting?.selectedImageHash
    ? castingCandidates.find(candidate => candidate.imageHash === workspace.casting?.selectedImageHash)
    : undefined;
  const selectedStyling = activeStyling?.selectedImageHash
    ? stylingCandidates.find(candidate => candidate.imageHash === activeStyling.selectedImageHash)
    : undefined;
  const currentSelectionKey = buildSelectionKey(
    projectId,
    characterName,
    activeStyling?.id,
    selectedCasting?.imageHash,
    selectedStyling?.imageHash,
  );
  currentSelectionKeyRef.current = currentSelectionKey;
  const working = !!currentSelectionKey && workingSelectionKey === currentSelectionKey;
  const canCreateStyling = useMemo(() => {
    if (!workspace) return false;
    const usedStates = new Set(workspace.stylings.map(styling => styling.state || ''));
    return workspace.states.some(state => !usedStates.has(state.state || ''));
  }, [workspace]);

  const defaultPrompt = useMemo(() => {
    if (!workspace || !activeStyling) return charFixedPrompt;
    return buildDefaultPrompt(
      charFixedPrompt,
      selectedCasting?.appearancePrompt || workspace.casting?.selectedAppearancePrompt || '',
      activeStyling.sourceAppearance,
      selectedStyling?.stylePrompt || activeStyling.selectedStylePrompt,
    );
  }, [workspace, activeStyling, charFixedPrompt, selectedCasting, selectedStyling]);

  const additionalRefs = useMemo(() => {
    const locked = [selectedCasting?.imageUrl, selectedStyling?.imageUrl].filter((url): url is string => !!url);
    return [...projectRefImages, ...uploadedRefs]
      .filter(url => !locked.includes(url))
      .filter(url => !removedAdditionalRefs.includes(url))
      .slice(0, Math.max(0, MAX_REFS - 2));
  }, [selectedCasting, selectedStyling, projectRefImages, uploadedRefs, removedAdditionalRefs]);

  const refs = useMemo(() => {
    const locked = [selectedCasting?.imageUrl, selectedStyling?.imageUrl].filter((url): url is string => !!url);
    return [...locked, ...additionalRefs].slice(0, MAX_REFS);
  }, [selectedCasting, selectedStyling, additionalRefs]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    promptInitializedRef.current = false;
    promptDirtyRef.current = false;
    promptRef.current = '';
    setPendingPrompt('');
    setLoading(true);
    getCharacterStyleWorkspace(projectId, characterName)
      .then(data => {
        if (cancelled) return;
        setWorkspace(data);
        notifyBaseMetadataChanged();
        const firstStyling = data.stylings[0];
        const initialModel = data.imageModels.find(item => item.platform === platform && item.model === model) || data.imageModels[0];
        setActiveStylingId(firstStyling?.id || '');
        setSelectedModelId(initialModel?.id || '');
      })
      .catch(err => {
        if (!cancelled) toast.error(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, projectId, characterName, platform, model, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEditingStylingDesc(false);
  }, [activeStylingId]);

  useEffect(() => {
    if (!open || !activeStyling) return;
    if (!promptInitializedRef.current) {
      promptInitializedRef.current = true;
      promptDirtyRef.current = false;
      promptRef.current = defaultPrompt;
      setPrompt(defaultPrompt);
      setPendingPrompt('');
      return;
    }
    if (promptDirtyRef.current && promptRef.current !== defaultPrompt) {
      setPendingPrompt(defaultPrompt);
      return;
    }
    promptDirtyRef.current = false;
    promptRef.current = defaultPrompt;
    setPendingPrompt('');
    setPrompt(defaultPrompt);
  }, [open, activeStyling?.id, defaultPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !activeStyling) {
      resultsRequestSeqRef.current += 1;
      setResults([]);
      return;
    }
    const requestSeq = resultsRequestSeqRef.current + 1;
    resultsRequestSeqRef.current = requestSeq;
    listCharacterStyleResults(projectId, characterName, activeStyling.id, {
      page: 1,
      limit: 100,
    }).then(response => {
      if (resultsRequestSeqRef.current === requestSeq) setResults(response.results || []);
    }).catch(() => {
      if (resultsRequestSeqRef.current === requestSeq) setResults([]);
    });
  }, [open, projectId, characterName, activeStyling]);

  const mergeWorkspace = (next: CharacterStyleWorkspaceData) => {
    setWorkspace(next);
    if (!next.stylings.some(styling => styling.id === activeStylingId)) {
      setActiveStylingId(next.stylings[0]?.id || '');
    }
  };

  // 精排进行中(running)时,在弹窗打开期间每 3s 重新拉取 workspace,直到 completed/failed。
  // 用纯 GET getCharacterStyleWorkspace,无副作用;completed/failed 时依赖变化使 effect 重建并清掉定时器。
  useEffect(() => {
    if (!aiRankOpen) return;
    if (workspace?.casting?.castingRankStatus !== 'running') return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getCharacterStyleWorkspace(projectId, characterName)
        .then(next => {
          if (!cancelled) mergeWorkspace(next);
          if (!cancelled) notifyBaseMetadataChanged();
        })
        .catch(() => {});
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [aiRankOpen, workspace?.casting?.castingRankStatus, projectId, characterName]); // eslint-disable-line react-hooks/exhaustive-deps

  const spinKeyframes = '@keyframes spin { to { transform: rotate(360deg); } }';

  const handleRequestSaveDescription = () => {
    if (castingBaseBlocked) {
      toast.error(t('characterStyle.baseMetadataConflict'));
      return;
    }
    const trimmed = editDescription.trim();
    if (!trimmed) {
      toast.error(t('characterStyle.descriptionEmpty'));
      return;
    }
    setConfirmDescEdit(true);
  };

  const handleConfirmSaveDescription = async () => {
    if (castingBaseBlocked) {
      setConfirmDescEdit(false);
      toast.error(t('characterStyle.baseMetadataConflict'));
      return;
    }
    const trimmed = editDescription.trim();
    setConfirmDescEdit(false);
    setEditingDescription(false);
    setSavingDescription(true);
    setWorkspace(prev => {
      if (!prev || !prev.casting) return prev;
      return {
        ...prev,
        casting: {
          ...prev.casting,
          sourceAppearance: trimmed,
          sourcePersonality: '',
          tagsStatus: 'running',
          tagsFlat: '',
          selectedImageHash: '',
          pageLoaded: 0,
          hasMore: true,
          fetchStatus: 'idle',
          fetchError: '',
          selectionTouched: false,
        },
        castingCandidates: [],
      };
    });
    try {
      const data = await updateCastingDescription(projectId, characterName, trimmed);
      mergeWorkspace(data);
      notifyBaseMetadataChanged();
    } catch (err: unknown) {
      reportCastingActionFailure(err);
    } finally {
      setSavingDescription(false);
    }
  };

  const handleRequestSaveStylingDesc = () => {
    const trimmed = editStylingDesc.trim();
    if (!trimmed) {
      toast.error(t('characterStyle.descriptionEmpty'));
      return;
    }
    setConfirmStylingDescEdit(true);
  };

  const handleConfirmSaveStylingDesc = async () => {
    const trimmed = editStylingDesc.trim();
    setConfirmStylingDescEdit(false);
    setEditingStylingDesc(false);
    setSavingStylingDesc(true);
    setWorkspace(prev => {
      if (!prev) return prev;
      const updatedCandidates = { ...prev.stylingCandidates };
      delete updatedCandidates[activeStylingId];
      return {
        ...prev,
        stylings: prev.stylings.map(s =>
          s.id === activeStylingId
            ? {
                ...s,
                sourceAppearance: trimmed,
                sourcePersonality: '',
                tagsStatus: 'running',
                tagsFlat: '',
                selectedImageHash: '',
                pageLoaded: 0,
                hasMore: true,
                fetchStatus: 'idle',
                fetchError: '',
                selectionTouched: false,
              }
            : s,
        ),
        stylingCandidates: updatedCandidates,
      };
    });
    try {
      const data = await updateStylingDescription(projectId, activeStylingId, characterName, trimmed);
      mergeWorkspace(data);
    } catch (err: unknown) {
      toast.error(t('characterStyle.descriptionUpdateFailed', { error: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSavingStylingDesc(false);
    }
  };

  useEffect(() => {
    if (!open || !isWorkspaceProcessing(workspace)) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      getCharacterStyleWorkspace(projectId, characterName)
        .then(next => {
          if (cancelled) return;
          setWorkspace(next);
          if (!next.stylings.some(styling => styling.id === activeStylingId)) {
            setActiveStylingId(next.stylings[0]?.id || '');
          }
          notifyBaseMetadataChanged();
        })
        .catch(() => {});
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [open, projectId, characterName, workspace, activeStylingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshResults = async () => {
    if (!activeStyling) return;
    const requestSeq = resultsRequestSeqRef.current + 1;
    resultsRequestSeqRef.current = requestSeq;
    const response = await listCharacterStyleResults(projectId, characterName, activeStyling.id, {
      page: 1,
      limit: 100,
    });
    if (resultsRequestSeqRef.current === requestSeq) {
      setResults(response.results || []);
    }
  };

  const handleGenerate = async () => {
    if (!activeStyling || !selectedCasting?.imageHash || !selectedStyling?.imageHash) return;
    const selectionKey = currentSelectionKey;
    if (!selectionKey) return;
    const selectedImageModel = workspace?.imageModels.find(item => item.id === selectedModelId);
    setWorkingSelectionKey(selectionKey);
    try {
      const response = await generateCharacterStyleResult(projectId, characterName, activeStyling.id, {
        castingImageHash: selectedCasting.imageHash,
        stylingImageHash: selectedStyling.imageHash,
        prompt,
        refImageUrls: refs,
        platform: selectedImageModel?.platform || platform || '',
        model: selectedImageModel?.model || model || '',
      });
      if (currentSelectionKeyRef.current === selectionKey) {
        setResults(prev => [response.result, ...prev.filter(item => item.id !== response.result.id)]);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setWorkingSelectionKey(prev => (prev === selectionKey ? '' : prev));
    }
  };

  const handleResultUpload = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    if (!activeStyling || !selectedCasting?.imageHash || !selectedStyling?.imageHash) return;
    const selectionKey = currentSelectionKey;
    if (!selectionKey) return;
    const response = await uploadCharacterStyleResult(projectId, characterName, activeStyling.id, selectedCasting.imageHash, selectedStyling.imageHash, file);
    if (currentSelectionKeyRef.current === selectionKey) {
      setResults(prev => [response.result, ...prev]);
    }
  };

  const handleSetRole = async (resultId: string, role: CharacterStyleResultRole) => {
    await setCharacterStyleResultRole(projectId, resultId, role);
    await refreshResults();
  };

  const handleCreateStyling = async () => {
    if (!workspace || !canCreateStyling || creatingStyling) return;
    const previousIds = new Set(workspace.stylings.map(styling => styling.id));
    setCreatingStyling(true);
    try {
      const next = await createCharacterStyling(projectId, characterName);
      setWorkspace(next);
      const created = next.stylings.find(styling => !previousIds.has(styling.id)) || next.stylings[next.stylings.length - 1];
      if (created) setActiveStylingId(created.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(t('characterStyle.addStylingFailed', { error: message }));
    } finally {
      setCreatingStyling(false);
    }
  };

  const handleUploadAdditionalRef = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    const response = await uploadAssetConfigRefImage(projectId, 'character', file);
    setUploadedRefs(prev => {
      // 用 prev（最新已上传集合）而非闭包 uploadedRefs 去重；projectRefImages 是源项目锁定参考图、批内不变
      const currentRefs = new Set([...projectRefImages, ...prev]);
      const addedRefs = response.projectRefImages.filter(url => !currentRefs.has(url));
      const durableRefs = addedRefs.length > 0 ? addedRefs : response.projectRefImages.slice(-1);
      return [...prev, ...durableRefs].slice(0, Math.max(0, MAX_REFS - 2));
    });
  };

  const handleRemoveAdditionalRef = (url: string) => {
    setRemovedAdditionalRefs(prev => (prev.includes(url) ? prev : [...prev, url]));
  };

  const additionalRefZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, MAX_REFS - refs.length),
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadAdditionalRef(f);
    },
  });
  const resultZone = useImageUploadZone({
    onFiles: (imgs) => handleResultUpload(imgs[0]),
  });

  const handleCancelPromptReplace = () => {
    setPendingPrompt('');
  };

  const handleConfirmPromptReplace = () => {
    promptDirtyRef.current = false;
    promptRef.current = pendingPrompt;
    setPrompt(pendingPrompt);
    setPendingPrompt('');
  };

  const imageModels = workspace?.imageModels || [];
  const castingTags = parseTagList(workspace?.casting?.tagsFlat);
  const stylingTags = parseTagList(activeStyling?.tagsFlat);

  const handleSaveCastingTags = async (tags: string[]) => {
    if (castingBaseBlocked) {
      toast.error(t('characterStyle.baseMetadataConflict'));
      return;
    }
    setSavingTags(true);
    try {
      const data = await updateCastingTags(projectId, characterName, tags);
      mergeWorkspace(data);
      notifyBaseMetadataChanged();
      setTagEditor(null);
    } catch (err) {
      reportCastingActionFailure(err);
    } finally {
      setSavingTags(false);
    }
  };

  const handleSaveStylingTags = async (tags: string[]) => {
    if (!activeStyling) return;
    setSavingTags(true);
    try {
      const data = await updateStylingTags(projectId, characterName, activeStyling.id, tags);
      mergeWorkspace(data);
      setTagEditor(null);
    } catch (err) {
      toast.error(t('characterStyle.tagsSaveFailed', { error: err instanceof Error ? err.message : String(err) }));
    } finally {
      setSavingTags(false);
    }
  };

  const handleRetryTags = async () => {
    if (castingBaseBlocked) {
      toast.error(t('characterStyle.baseMetadataConflict'));
      return;
    }
    try {
      const data = await retryCharacterStyleTags(projectId, characterName);
      mergeWorkspace(data);
      notifyBaseMetadataChanged();
    } catch (error) {
      reportCastingActionFailure(error);
    }
  };

  const activeStateLabel = activeStyling?.state || t('character.defaultState');

  return (
    <>
      <Modal open={open} onClose={onClose} title={t('characterStyle.title', { name: characterName })} width="96vw">
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-2)' }}>
            <span className="loading-spinner" />
            {t('characterStyle.loading')}
          </div>
        ) : workspace ? (
          <>
          <style>{spinKeyframes}</style>
          {castingBaseBlocked && (
            <p data-testid="character-style-base-conflict" style={{ margin: '0 0 var(--space-sm)', color: 'var(--danger)', fontSize: '0.82rem' }}>
              {t('characterStyle.baseMetadataConflict')}
            </p>
          )}
          <div
            data-testid="character-style-design-dialog"
            onWheel={e => e.stopPropagation()}
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '364px minmax(420px, 1fr) minmax(256px, 0.95fr)',
              gap: 'var(--space-base)',
              height: isMobile ? 'auto' : 'calc(90vh - 120px)',
              overflow: isMobile ? 'visible' : 'hidden',
            }}
          >
            <aside
              data-testid="character-style-profile-panel"
              style={{
                display: 'flex',
                flexDirection: 'column' as const,
                gap: 'var(--space-md)',
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <section
                onMouseEnter={() => setDescriptionHovered(true)}
                onMouseLeave={() => setDescriptionHovered(false)}
                className="theme-left-border"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-base)', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', position: 'relative', flex: '0 1 auto', maxHeight: '45%', minHeight: 0, overflow: 'hidden', '--_theme-color': 'var(--theme-character)', '--_theme-color-end': '#60a5fa' } as React.CSSProperties}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{workspace.characterName}</h3>
                  {!editingDescription && (
                    <button
                      type="button"
                      title={t('characterStyle.editDescription')}
                      onClick={() => {
                        if (castingBaseBlocked) {
                          toast.error(t('characterStyle.baseMetadataConflict'));
                          return;
                        }
                        setEditDescription(
                          [workspace.casting?.sourceAppearance, workspace.casting?.sourcePersonality].filter(Boolean).join('\n'),
                        );
                        setEditingDescription(true);
                      }}
                      disabled={castingBaseBlocked}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  )}
                </div>
                {editingDescription ? (
                  <>
                    <textarea
                      className="input"
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      rows={6}
                      style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.84rem', lineHeight: 1.55 }}
                      disabled={savingDescription}
                    />
                    <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setEditingDescription(false)}
                        disabled={savingDescription}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {t('characterStyle.cancelEdit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleRequestSaveDescription}
                        disabled={savingDescription || castingBaseBlocked}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {savingDescription ? t('characterStyle.loading') : t('characterStyle.saveDescription')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--text-2)', fontSize: '0.84rem', lineHeight: 1.55, wordBreak: 'break-word', overflowY: 'auto', minHeight: 0 }}>
                    {[workspace.casting?.sourceAppearance, workspace.casting?.sourcePersonality].filter(Boolean).join('\n')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginTop: '10px', alignItems: 'center' }}>
                  {PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '') ? (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid var(--border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      {t('characterStyle.generatingCastingTags')}
                    </span>
                  ) : workspace.casting?.tagsStatus === 'failed' ? (
                    <span style={{ fontSize: '0.75rem', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {t('characterStyle.castingTagsFailed')}
                      <button
                        type="button"
                        onClick={handleRetryTags}
                        disabled={castingBaseBlocked}
                        style={{ background: 'none', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.68rem', padding: '1px 6px' }}
                      >
                        {t('characterStyle.retry')}
                      </button>
                    </span>
                  ) : (
                    castingTags.slice(0, 16).map(tag => (
                      <span key={tag} style={{ borderRadius: '10px', background: '#eff6ff', color: '#1e40af', padding: '2px 7px', fontSize: '0.68rem', fontWeight: 500, border: '1px solid #93c5fd' }}>{tag}</span>
                    ))
                  )}
                  <button
                    type="button"
                    title={t('characterStyle.editTags')}
                    onClick={() => setTagEditor('casting')}
                    disabled={castingBaseBlocked || PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '')}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-2)', cursor: PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '') ? 'not-allowed' : 'pointer', opacity: PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '') ? 0.4 : 1, fontSize: '0.68rem', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    {t('characterStyle.editTags')}
                  </button>
                </div>
              </section>

              <section className="theme-left-border" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-md)', display: 'flex', flexDirection: 'column', flex: '1 1 55%', minHeight: 0, '--_theme-color': 'var(--theme-character)', '--_theme-color-end': '#60a5fa' } as React.CSSProperties}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-sm)' }}>
                  <span style={{ fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{t('characterStyle.casting')}</span>
                  <button type="button" onClick={() => setPicker('casting')} disabled={PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '')} style={{ background: 'none', border: '1px solid var(--primary)', borderRadius: '4px', color: 'var(--primary)', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem', padding: '2px 10px', transition: 'opacity var(--transition-fast)', opacity: PROCESSING_TAG_STATUSES.has(workspace.casting?.tagsStatus || '') ? 0.4 : 1 }}>
                    {t('characterStyle.change')}
                  </button>
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', color: 'var(--text-2)', fontSize: '0.9rem', minHeight: 0, overflow: 'hidden' }}>
                  {selectedCasting ? (
                    <img src={tosThumbnail(selectedCasting.imageUrl, 360)} alt={t('characterStyle.casting')} style={{ width: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 'var(--radius)' }} onError={e => { (e.target as HTMLImageElement).style.opacity = '0.15'; }} />
                  ) : (
                    t('characterStyle.noCastingSelected')
                  )}
                </div>
              </section>
            </aside>

            <main data-testid="character-style-composer-panel" style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: 0, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
              <nav className="hide-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingTop: '18px', zIndex: 1, overflowY: 'scroll', minHeight: 0 }}>
                {workspace.stylings.map((styling, index) => {
                  const isActive = styling.id === activeStyling?.id;
                  return (
                    <button
                      key={styling.id}
                      type="button"
                      onClick={() => setActiveStylingId(styling.id)}
                      title={`${t('characterStyle.styling')} ${index + 1}`}
                      data-testid={isActive ? 'active-styling-tab' : undefined}
                      style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'upright' as const,
                        background: isActive ? '#7c3aed' : 'var(--surface-2)',
                        border: '1px solid var(--border)',
                        borderRight: isActive ? '1px solid #7c3aed' : '1px solid var(--border)',
                        borderRadius: '8px 0 0 8px',
                        marginRight: '0px',
                        color: isActive ? '#fff' : 'var(--text-2)',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.9rem',
                        letterSpacing: '0.05em',
                        cursor: 'pointer',
                        padding: '12px 6px',
                        transition: 'color var(--transition-fast), background var(--transition-fast)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('characterStyle.styling')}{index + 1}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={handleCreateStyling}
                  disabled={!canCreateStyling || creatingStyling}
                  title={!canCreateStyling ? t('characterStyle.addStylingDisabled') : undefined}
                  style={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'upright' as const,
                    background: 'var(--surface-2)',
                    border: '1px dashed #a78bfa',
                    borderRight: '1px dashed #a78bfa',
                    borderRadius: '8px 0 0 8px',
                    marginRight: '0px',
                    color: '#7c3aed',
                    fontSize: '0.9rem',
                    letterSpacing: '0.05em',
                    cursor: canCreateStyling && !creatingStyling ? 'pointer' : 'not-allowed',
                    padding: '12px 6px',
                    opacity: canCreateStyling ? 1 : 0.4,
                    transition: 'color var(--transition-fast)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {creatingStyling ? t('characterStyle.creatingStylingShort') : t('characterStyle.addStylingShort')}
                </button>
              </nav>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                <section className="theme-left-border" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-base)', minWidth: 0, '--_theme-color': '#7c3aed', '--_theme-color-end': '#a78bfa' } as React.CSSProperties}>
                  <div
                    onMouseEnter={() => setStylingDescHovered(true)}
                    onMouseLeave={() => setStylingDescHovered(false)}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'flex-start', marginBottom: 'var(--space-sm)' }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{activeStateLabel}</h3>
                        {!editingStylingDesc && (
                          <button
                            type="button"
                            title={t('characterStyle.editStylingDescription')}
                            onClick={() => {
                              setEditStylingDesc(
                                [activeStyling?.sourceAppearance, activeStyling?.sourcePersonality].filter(Boolean).join('\n'),
                              );
                              setEditingStylingDesc(true);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-2)', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center', flexShrink: 0 }}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        )}
                      </div>
                      {editingStylingDesc ? (
                        <>
                          <textarea
                            className="input"
                            value={editStylingDesc}
                            onChange={e => setEditStylingDesc(e.target.value)}
                            rows={4}
                            style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.84rem', lineHeight: 1.55, marginTop: 'var(--space-xs)' }}
                            disabled={savingStylingDesc}
                          />
                          <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end', marginTop: 'var(--space-xs)' }}>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setEditingStylingDesc(false)}
                              disabled={savingStylingDesc}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {t('characterStyle.cancelEdit')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={handleRequestSaveStylingDesc}
                              disabled={savingStylingDesc}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {savingStylingDesc ? t('characterStyle.loading') : t('characterStyle.saveDescription')}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div style={{ color: 'var(--text-2)', fontSize: '0.84rem', lineHeight: 1.55, marginTop: 'var(--space-xs)', wordBreak: 'break-word', maxHeight: 'calc(0.84rem * 1.55 * 5)', overflowY: 'auto' }}>
                          {[activeStyling?.sourceAppearance, activeStyling?.sourcePersonality].filter(Boolean).join(' / ')}
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <div data-testid="character-style-styling-tags" style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
                      {PROCESSING_TAG_STATUSES.has(activeStyling?.tagsStatus || '') ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid var(--border)', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          {t('characterStyle.generatingStylingTags')}
                        </span>
                      ) : activeStyling?.tagsStatus === 'failed' ? (
                        <span style={{ fontSize: '0.75rem', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {t('characterStyle.stylingTagsFailed')}
                          <button
                            type="button"
                            onClick={handleRetryTags}
                            disabled={castingBaseBlocked}
                            style={{ background: 'none', border: '1px solid var(--danger)', borderRadius: '4px', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.68rem', padding: '1px 6px' }}
                          >
                            {t('characterStyle.retry')}
                          </button>
                        </span>
                      ) : (
                        stylingTags.map(tag => (
                          <span key={tag} style={{ borderRadius: '10px', background: '#ede9fe', color: '#5b21b6', padding: '2px 7px', fontSize: '0.68rem', fontWeight: 500, border: '1px solid #c4b5fd' }}>{tag}</span>
                        ))
                      )}
                      {activeStyling && (
                        <button
                          type="button"
                          title={t('characterStyle.editTags')}
                          onClick={() => setTagEditor('styling')}
                          disabled={PROCESSING_TAG_STATUSES.has(activeStyling?.tagsStatus || '')}
                          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-2)', cursor: PROCESSING_TAG_STATUSES.has(activeStyling?.tagsStatus || '') ? 'not-allowed' : 'pointer', opacity: PROCESSING_TAG_STATUSES.has(activeStyling?.tagsStatus || '') ? 0.4 : 1, fontSize: '0.68rem', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          {t('characterStyle.editTags')}
                        </button>
                      )}
                    </div>
                    <div data-testid="character-style-preview-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
                      <span style={{ fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{t('characterStyle.generatePreview')}</span>
                      <select className="input" value={selectedModelId} onChange={event => setSelectedModelId(event.target.value)} style={{ width: 160, flex: '0 0 160px', fontSize: '0.75rem', padding: '3px 6px', height: '28px' }}>
                        {imageModels.map(item => <option key={item.id} value={item.id}>{item.displayName || item.model}</option>)}
                      </select>
                    </div>
                    <textarea
                      data-testid="character-style-prompt"
                      className="input"
                      value={prompt}
                      onChange={event => {
                        const nextPrompt = event.target.value;
                        promptDirtyRef.current = true;
                        promptRef.current = nextPrompt;
                        setPrompt(nextPrompt);
                      }}
                      rows={3}
                      style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.84rem', lineHeight: 1.55, padding: '0.2rem 0.75rem' }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', marginTop: 'var(--space-md)', flexWrap: 'wrap' }}>
                    {[
                      {
                        key: 'casting',
                        url: selectedCasting?.imageUrl,
                        label: t('characterStyle.lockedCastingRef', { n: 1 }),
                        testId: 'locked-casting-ref',
                      },
                      {
                        key: 'styling',
                        url: selectedStyling?.imageUrl,
                        label: t('characterStyle.lockedStylingRef', { n: 2 }),
                        testId: 'locked-styling-ref',
                      },
                    ].map(item => (
                      <div
                        key={item.key}
                        data-testid={item.testId}
                        title={item.label}
                        style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', border: item.key === 'casting' ? '2px solid #f59e0b' : '2px solid #7c3aed', background: 'var(--surface)', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', fontSize: '0.68rem', textAlign: 'center', lineHeight: 1.15 }}
                      >
                        {item.url ? (
                          <img src={tosThumbnail(item.url, 80)} alt={item.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          t('character.toSelect')
                        )}
                      </div>
                    ))}
                    {additionalRefs.map((url, index) => (
                      <div
                        key={`${url}-${index}`}
                        style={{ width: 40, height: 40, position: 'relative', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface)' }}
                      >
                        <img
                          src={tosThumbnail(url, 80)}
                          alt={t('characterStyle.refAlt', { n: index + 3 })}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-sm)' }}
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <button
                          type="button"
                          aria-label={t('characterStyle.removeRef')}
                          title={t('characterStyle.removeRef')}
                          onClick={() => handleRemoveAdditionalRef(url)}
                          style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 9, border: '1px solid var(--border)', background: '#fff', color: 'var(--text-2)', fontSize: 12, lineHeight: '16px', padding: 0, cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {refs.length < MAX_REFS && (
                      <label {...additionalRefZone.zoneProps} style={{ width: 40, height: 40, border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-3)', background: 'var(--surface)', transition: 'border-color var(--transition-fast)', ...getDropHighlightStyle(additionalRefZone.isOver) }}>
                        +
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={async event => {
                            const file = event.target.files?.[0];
                            if (file) await handleUploadAdditionalRef(file);
                            event.target.value = '';
                          }}
                        />
                      </label>
                    )}
                    <button className="btn" onClick={handleGenerate} disabled={working || !selectedCasting || !selectedStyling} style={{ marginLeft: 'auto', minWidth: 86, textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #06b6d4, #3b82f6)', color: '#fff', border: 'none' }}>
                      {working ? t('characterStyle.submitting') : t('characterStyle.generate')}
                    </button>
                  </div>
                </section>

                <section className="theme-left-border" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-md)', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, '--_theme-color': '#7c3aed', '--_theme-color-end': '#a78bfa' } as React.CSSProperties}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-sm)' }}>
                    <span style={{ fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{t('characterStyle.styling')}</span>
                    <button type="button" onClick={() => setPicker('styling')} disabled={!activeStyling} style={{ background: 'none', border: '1px solid var(--primary)', borderRadius: '4px', color: 'var(--primary)', cursor: activeStyling ? 'pointer' : 'not-allowed', fontWeight: 500, fontSize: '0.75rem', padding: '2px 10px', opacity: activeStyling ? 1 : 0.4, transition: 'opacity var(--transition-fast)' }}>
                      {t('characterStyle.change')}
                    </button>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', color: 'var(--text-2)', fontSize: '0.9rem', minHeight: 0, overflow: 'hidden' }}>
                    {selectedStyling ? (
                      <img src={tosThumbnail(selectedStyling.imageUrl, 420)} alt={t('characterStyle.styling')} style={{ height: '100%', maxWidth: '100%', objectFit: 'contain', borderRadius: 'var(--radius)' }} onError={e => { (e.target as HTMLImageElement).style.opacity = '0.15'; }} />
                    ) : (
                      t('characterStyle.noStylingSelected')
                    )}
                  </div>
                </section>
              </div>
            </main>

            <section
              data-testid="character-style-results-panel"
              className="theme-left-border"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-md)', minWidth: 0, minHeight: 0, display: 'grid', gridTemplateRows: 'auto 1fr', overflow: 'hidden', '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6' } as React.CSSProperties}
            >
              <div style={{ marginBottom: 'var(--space-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '0.86rem', color: 'var(--text)', fontWeight: 600 }}>{t('characterStyle.resultHistory')}</h3>
                <label {...resultZone.zoneProps} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-xs)', cursor: 'pointer', color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 500, border: '1px solid var(--primary)', borderRadius: '4px', padding: '2px 10px', ...getDropHighlightStyle(resultZone.isOver) }}>
                  + {t('characterStyle.uploadResult')}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) await handleResultUpload(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              <div style={{ overflow: 'auto', minHeight: 0 }}>
                <CharacterStyleHistoryGrid results={results} castingCandidates={castingCandidates} allStylingCandidates={workspace?.stylingCandidates} onSetRole={handleSetRole} onUpload={handleResultUpload} />
              </div>
            </section>
          </div>
          </>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={confirmDescEdit}
        onCancel={() => setConfirmDescEdit(false)}
        onConfirm={handleConfirmSaveDescription}
        title={t('characterStyle.editDescription')}
        message={t('characterStyle.editDescriptionConfirm')}
        loading={savingDescription}
      />

      <ConfirmDialog
        open={confirmStylingDescEdit}
        onCancel={() => setConfirmStylingDescEdit(false)}
        onConfirm={handleConfirmSaveStylingDesc}
        title={t('characterStyle.editStylingDescription')}
        message={t('characterStyle.editStylingDescriptionConfirm')}
        loading={savingStylingDesc}
      />

      <ConfirmDialog
        open={!!pendingPrompt}
        onCancel={handleCancelPromptReplace}
        onConfirm={handleConfirmPromptReplace}
        title={t('characterStyle.generatePreview')}
        message={t('characterStyle.replacePromptConfirm')}
      />

      {workspace && (
        <CharacterStylePickerModal
          open={picker === 'casting'}
          title={t('characterStyle.casting')}
          candidates={castingCandidates}
          selectedImageHash={workspace.casting?.selectedImageHash}
          hasMore={!!workspace.casting?.hasMore}
          loading={workspace.casting?.fetchStatus === 'fetching'}
          dependentActionsDisabled={castingBaseBlocked}
          dependentActionsDisabledReason={castingBaseBlocked ? t('characterStyle.baseMetadataConflict') : undefined}
          onFetchMore={async () => {
            if (castingBaseBlocked) return;
            const prev = castingCandidates.length;
            try {
              const next = await fetchCastingCandidates(projectId, characterName);
              mergeWorkspace(next);
              notifyBaseMetadataChanged();
              if ((next.castingCandidates?.length || 0) <= prev) toast.info(t('characterStyle.noMore'));
            } catch (error) {
              reportCastingActionFailure(error);
            }
          }}
          onAiRank={async () => {
            setAiRankOpen(true);
            setAiRankSelected(undefined);
            try {
              const ws = await rankCastingCandidates(projectId, characterName, false);
              mergeWorkspace(ws);
            } catch (err: unknown) {
              toast.error(err instanceof Error ? err.message : String(err));
            }
          }}
          onUpload={async file => {
            if (castingBaseBlocked) return;
            try {
              mergeWorkspace(await uploadCastingCandidate(projectId, characterName, file));
              notifyBaseMetadataChanged();
            } catch (error) {
              reportCastingActionFailure(error);
            }
          }}
          onSelect={async hash => {
            if (castingBaseBlocked) return;
            try {
              mergeWorkspace(await selectCastingCandidate(projectId, characterName, hash));
              notifyBaseMetadataChanged();
            } catch (error) {
              reportCastingActionFailure(error);
            }
          }}
          onConfirm={() => setPicker(null)}
          onClose={() => setPicker(null)}
        />
      )}
      {(() => {
        const rankItems: CharacterAIRankItem[] = (() => {
          const raw = workspace?.casting?.castingRankJson;
          if (!raw) return [];
          try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? (parsed as CharacterAIRankItem[]) : [];
          } catch {
            return [];
          }
        })();
        const status = workspace?.casting?.castingRankStatus;
        const effectiveSelected = aiRankSelected ?? (status === 'completed' ? rankItems[0]?.imageHash : undefined);
        return (
          <CharacterAIRankModal
            open={aiRankOpen}
            status={status}
            error={workspace?.casting?.castingRankError}
            items={rankItems}
            selectedHash={castingBaseBlocked ? undefined : effectiveSelected}
            onSelect={castingBaseBlocked ? () => {} : setAiRankSelected}
            onRerank={async () => {
              setAiRankSelected(undefined);
              try {
                const ws = await rankCastingCandidates(projectId, characterName, true);
                mergeWorkspace(ws);
              } catch (err: unknown) {
                toast.error(err instanceof Error ? err.message : String(err));
              }
            }}
            onConfirm={async () => {
              if (!effectiveSelected || castingBaseBlocked) return;
              try {
                const ws = await selectCastingCandidate(projectId, characterName, effectiveSelected);
                mergeWorkspace(ws);
                notifyBaseMetadataChanged();
                setAiRankOpen(false);
                setPicker(null);
              } catch (err: unknown) {
                reportCastingActionFailure(err);
              }
            }}
            onClose={() => setAiRankOpen(false)}
          />
        );
      })()}
      {workspace && activeStyling && (
        <CharacterStylePickerModal
          open={picker === 'styling'}
          title={t('characterStyle.styling')}
          candidates={stylingCandidates}
          selectedImageHash={activeStyling.selectedImageHash}
          hasMore={activeStyling.hasMore}
          loading={activeStyling.fetchStatus === 'fetching'}
          onFetchMore={async () => {
            const prev = stylingCandidates.length;
            const next = await fetchStylingCandidates(projectId, characterName, activeStyling.id);
            mergeWorkspace(next);
            const nextCount = next.stylingCandidates?.[activeStyling.id]?.length || 0;
            if (nextCount <= prev) toast.info(t('characterStyle.noMore'));
          }}
          onUpload={async file => mergeWorkspace(await uploadStylingCandidate(projectId, characterName, activeStyling.id, file))}
          onSelect={async hash => mergeWorkspace(await selectStylingCandidate(projectId, characterName, activeStyling.id, hash))}
          onConfirm={() => {
            setPicker(null);
            refreshResults();
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {workspace?.casting && (
        <CharacterTagEditorModal
          open={tagEditor === 'casting'}
          kind="casting"
          title={t('characterStyle.editCastingTagsTitle')}
          currentTags={castingTags}
          saving={savingTags}
          onSave={handleSaveCastingTags}
          onClose={() => setTagEditor(null)}
        />
      )}
      {activeStyling && (
        <CharacterTagEditorModal
          open={tagEditor === 'styling'}
          kind="styling"
          title={t('characterStyle.editStylingTagsTitle')}
          currentTags={stylingTags}
          saving={savingTags}
          onSave={handleSaveStylingTags}
          onClose={() => setTagEditor(null)}
        />
      )}
    </>
  );
}
